import { useCallback, useEffect } from 'react'
import { flushSync } from 'react-dom'
import { useAppStore } from 'v2/state/app'

export const useDarkMode = (): [boolean, () => void] => {
  const isDarkMode = useAppStore((state) => state.isDarkMode)
  const setIsDarkMode = useAppStore((state) => state.setDarkMode)

  // Stable across renders: consumers (e.g. the command palette) memoize command lists off
  // this, and an unstable reference here caused a real render loop with useCommandRegister.
  const applyDarkMode = useCallback(
    (mode: boolean) => {
      setIsDarkMode(mode)
      localStorage.setItem('darkMode', String(mode))
      document.documentElement.classList.toggle('dark', mode)
    },
    [setIsDarkMode],
  )

  useEffect(() => {
    const storedMode = localStorage.getItem('darkMode') === 'true'
    applyDarkMode(storedMode)
  }, [])

  // Animated circular reveal from the top-right corner (where the theme toggle lives) —
  // Magic UI animated-theme-toggler pattern, View Transitions API. Falls back to an
  // instant switch where unsupported (Safari < 18, happy-dom in tests).
  const toggleDarkMode = useCallback((): void => {
    const next = !isDarkMode
    if (!document.startViewTransition) {
      applyDarkMode(next)
      return
    }
    const transition = document.startViewTransition(() => {
      // The class flip must be committed to the DOM before the browser snapshots
      // the "new" state, hence flushSync.
      flushSync(() => applyDarkMode(next))
    })
    transition.ready.then(() => {
      const x = window.innerWidth * 2
      const y = 0
      // Overscan the end radius (2.5x the distance to the farthest corner): the screen is
      // fully covered mid-animation, before the ease-out tail, so the wavefront sweeps the
      // whole viewport at a steady pace instead of stalling near the origin and then
      // snapping shut at the end.
      const maxRadius = 2.5 * Math.hypot(x, window.innerHeight)
      document.documentElement.animate(
        {
          clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`],
        },
        { duration: 900, easing: 'ease-in-out', pseudoElement: '::view-transition-new(root)' },
      )
    })
  }, [applyDarkMode, isDarkMode])

  return [isDarkMode, toggleDarkMode]
}
